import React, { useState, useEffect } from "react";
import { ArrowUpRight, Mail, Phone, ShoppingCart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { useContact } from "@/context/ContactContext";
import { useProductDetail } from "@/context/ProductDetailContext";
import { useClient } from "@/context/ClientContext";
import { BRAND } from "@/data/brand";
import { pText, techText } from "@/data/productDe";
import { STOCK_ROWS, eur } from "@/data/clientAreaDemo";

const TONES = {
  green: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  amber: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  blue: "bg-sky-400/10 text-sky-300 border-sky-400/30",
};

export default function ProductDetail() {
  const { lang, t } = useLang();
  const { product, open, setOpen } = useProductDetail();
  const { openContact } = useContact();
  const { client } = useClient();
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setActive(0);
    setQty(1);
  }, [product]);

  if (!product) return null;

  const d = t.catalog.detail;
  const name = pText(product, lang, "name");
  const images =
    product.gallery && product.gallery.length > 0
      ? product.gallery
      : [product.image].filter(Boolean);
  const techEntries = product.tech
    ? Object.entries(product.tech).filter(([k, v]) => v && d.labels[k])
    : [];

  const row = client ? STOCK_ROWS.find((r) => r.id === product.id) : null;
  const stockState = row
    ? row.stock === 0
      ? { tone: "blue", label: t.area.st.out }
      : row.stock < 4
        ? { tone: "amber", label: t.area.st.low }
        : { tone: "green", label: t.area.st.in }
    : null;

  const requestInfo = () => {
    setOpen(false);
    openContact({ ref: product.ref, name, id: product.id });
  };

  const orderHref = () => {
    const q = Math.max(1, parseInt(qty, 10) || 1);
    const body = [
      `- ${q} x ${product.ref} — ${name} (${eur(row.price)}/u)`,
      `  ${t.area.th.total}: ${eur(row.price * q)}`,
      ``,
      `${client.name} — ${client.company}`,
    ].join("\n");
    return `mailto:${BRAND.email}?subject=${encodeURIComponent(`${t.area.reqSubject}: ${product.ref}`)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="product-detail"
        className="bg-[#112318] border border-white/12 rounded-none text-white max-w-4xl max-h-[92vh] overflow-y-auto p-0"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Gallery */}
          <div className="bg-[#0B1712] p-5 sm:p-6">
            <div className="aspect-[4/3] overflow-hidden border border-white/10 bg-black/30">
              <img
                key={active}
                src={images[active]}
                alt={name}
                data-testid="detail-main-image"
                className="h-full w-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`detail-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`h-16 w-20 overflow-hidden border transition-colors ${
                      active === i
                        ? "border-[color:var(--dp-orange)]"
                        : "border-white/12 opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left space-y-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="font-mono text-[11px] px-2 py-1 bg-[color:var(--dp-orange)] text-black">
                  {t.catalog.ref} {product.ref}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                  {product.category === "dpf" ? t.catalog.dpf : t.catalog.catalyst}
                </span>
              </div>
              <DialogTitle className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight leading-tight">
                {name}
              </DialogTitle>
              <DialogDescription className="text-white/65 font-light leading-relaxed pt-3">
                {pText(product, lang, "desc")}
              </DialogDescription>
            </DialogHeader>

            {product.specs && product.specs.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {product.specs.map((s) => (
                  <span
                    key={s}
                    className="font-mono text-[10px] uppercase tracking-widest text-white/60 border border-white/12 px-2 py-1"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}

            {techEntries.length > 0 && (
              <div className="mt-6">
                <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--dp-orange)] mb-3">
                  {d.tech}
                </h4>
                <dl className="divide-y divide-white/8 border-y border-white/8" data-testid="detail-tech">
                  {techEntries.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 py-2.5">
                      <dt className="text-white/50 text-sm">{d.labels[k]}</dt>
                      <dd className="text-white text-sm font-medium text-right">{techText(lang, v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {product.oem && product.oem.length > 0 && (
              <div className="mt-6">
                <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">
                  {d.oemTitle}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {product.oem.map((code) => (
                    <span
                      key={code}
                      className="font-mono text-[11px] px-2 py-1 border border-white/12 text-white/70"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {client && row ? (
              <div className="mt-8 border border-white/12 bg-white/[0.03] p-5">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">
                      {d.price}
                    </p>
                    <p data-testid="detail-price" className="font-heading text-3xl font-black text-[color:var(--dp-orange)] mt-1">
                      {eur(row.price)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/45">
                      {d.stock}
                    </p>
                    <p className="mt-1.5">
                      <span data-testid="detail-stock" className="text-white/80 text-sm mr-2">{row.stock}</span>
                      <span
                        data-testid="detail-stock-badge"
                        className={`inline-flex px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest border ${TONES[stockState.tone]}`}
                      >
                        {stockState.label}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex items-stretch gap-3">
                  <input
                    data-testid="detail-qty"
                    type="number"
                    min="1"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    aria-label={t.area.th.qty}
                    className="w-20 bg-black/30 border border-white/15 px-3 py-3 text-sm text-center outline-none focus:border-[color:var(--dp-orange)] transition-colors"
                  />
                  <a
                    data-testid="detail-order"
                    href={orderHref()}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold transition-colors"
                  >
                    <ShoppingCart size={17} /> {d.order}
                  </a>
                </div>
              </div>
            ) : (
              <button
                data-testid="detail-request"
                onClick={requestInfo}
                className="mt-8 inline-flex items-center justify-between gap-2 w-full px-5 py-3.5 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold transition-colors"
              >
                {d.request}
                <ArrowUpRight size={18} />
              </button>
            )}

            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <a
                href={`mailto:${BRAND.email}?subject=${encodeURIComponent(name + " (" + product.ref + ")")}`}
                className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <Mail size={15} /> {BRAND.email}
              </a>
              {BRAND.phone && (
                <a
                  href={`tel:${BRAND.phone}`}
                  className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                >
                  <Phone size={15} /> {BRAND.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
